/**
 * Base API Service
 *
 * Centralized HTTP client for API communication.
 * Provides request/response interceptors, authentication, and error handling.
 *
 * [A011] Enhanced with authentication interceptors and callbacks
 */

import type {
  CardsStartRequest,
  CardsStartResponse,
  CardsTrialRequest,
  CardsTrialResponse,
  CardsFinishRequest,
  CardsFinishResponse,
  TreasureStartRequest,
  TreasureStartResponse,
  TreasureTrialRequest,
  TreasureTrialResponse,
  TreasureFinishRequest,
  TreasureFinishResponse,
} from '../types/api'

/**
 * API configuration
 */
export interface ApiConfig {
  baseURL: string
  timeout: number
  headers: Record<string, string>
}

/**
 * Authentication error callback
 * Called when API returns 401 (unauthorized) or 403 (forbidden)
 */
export type AuthErrorCallback = (error: ApiError) => void | Promise<void>

/**
 * HTTP method types
 */
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

/**
 * Request options
 */
export interface RequestOptions {
  method?: HttpMethod
  headers?: Record<string, string>
  body?: any
  timeout?: number
}

/**
 * API Error class
 */
export class ApiError extends Error {
  status: number
  statusText: string
  data?: any

  constructor(message: string, status: number, statusText: string, data?: any) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.statusText = statusText
    this.data = data
  }
}

/**
 * Base API Service class
 */
export class ApiService {
  private config: ApiConfig
  private authToken: string | null = null
  private authErrorCallback: AuthErrorCallback | null = null

  constructor(config?: Partial<ApiConfig>) {
    this.config = {
      baseURL: this.getBaseURL(),
      timeout: 30000, // 30 seconds
      headers: {
        'Content-Type': 'application/json',
      },
      ...config,
    }
  }

  /**
   * Get API base URL based on environment
   */
  private getBaseURL(): string {
    if (typeof window !== 'undefined') {
      // In browser, use relative URLs which will be proxied by nginx
      return '/api'
    }
    // Fallback for server-side rendering
    return import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'
  }

  /**
   * Set authentication token
   *
   * [A011] Authentication interceptor - automatically adds token to requests
   */
  setAuthToken(token: string | null) {
    this.authToken = token
    console.log('[API Service] Auth token', token ? 'set' : 'cleared')
  }

  /**
   * Get authentication token
   */
  getAuthToken(): string | null {
    return this.authToken
  }

  /**
   * Register callback for authentication errors
   *
   * [A011] Authentication interceptor - handles 401/403 errors
   *
   * @example
   * apiService.onAuthError((error) => {
   *   console.log('Auth failed, redirecting to login...')
   *   authStore.clearSession()
   * })
   */
  onAuthError(callback: AuthErrorCallback | null) {
    this.authErrorCallback = callback
  }

  /**
   * Build headers with authentication
   */
  private buildHeaders(customHeaders?: Record<string, string>): Record<string, string> {
    const headers = {
      ...this.config.headers,
      ...customHeaders,
    }

    // Add authorization header if token exists
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`
    }

    return headers
  }

  /**
   * Make HTTP request
   *
   * [A011] Enhanced with authentication error interceptor
   */
  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const {
      method = 'GET',
      headers: customHeaders,
      body,
      timeout = this.config.timeout,
    } = options

    const url = `${this.config.baseURL}${endpoint}`
    const headers = this.buildHeaders(customHeaders)

    // Create abort controller for timeout
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      // Handle non-2xx responses
      if (!response.ok) {
        const errorData = await response.json().catch(() => null)
        const apiError = new ApiError(
          errorData?.error || errorData?.message || response.statusText,
          response.status,
          response.statusText,
          errorData
        )

        // [A011] Authentication error interceptor
        // Trigger callback for 401 (Unauthorized) or 403 (Forbidden)
        if ((response.status === 401 || response.status === 403) && this.authErrorCallback) {
          console.error('[API Service] Authentication error detected:', response.status)
          await this.authErrorCallback(apiError)
        }

        throw apiError
      }

      // Parse response
      const data = await response.json()
      return data as T
    } catch (error) {
      clearTimeout(timeoutId)

      // Handle timeout
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiError('Request timeout', 408, 'Request Timeout')
      }

      // Handle network errors
      if (error instanceof TypeError) {
        throw new ApiError(
          'Network error - please check your connection',
          0,
          'Network Error'
        )
      }

      // Re-throw ApiError
      if (error instanceof ApiError) {
        throw error
      }

      // Unknown error
      throw new ApiError(
        error instanceof Error ? error.message : 'Unknown error occurred',
        500,
        'Internal Server Error'
      )
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' })
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, body?: any, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body })
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, body?: any, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body })
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' })
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, body?: any, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body })
  }

  // ============================================================================
  // Cards Game API Methods
  // ============================================================================

  /**
   * Start a new cards game
   */
  async cardsStart(request: CardsStartRequest): Promise<CardsStartResponse> {
    return this.post<CardsStartResponse>('/games/cards/start', request)
  }

  /**
   * Submit a cards trial
   */
  async cardsTrial(request: CardsTrialRequest): Promise<CardsTrialResponse> {
    return this.post<CardsTrialResponse>('/games/cards/trial', request)
  }

  /**
   * Finish cards game
   */
  async cardsFinish(request: CardsFinishRequest): Promise<CardsFinishResponse> {
    return this.post<CardsFinishResponse>('/games/cards/finish', request)
  }

  // ============================================================================
  // Treasure Game API Methods
  // ============================================================================

  /**
   * Start a new treasure game
   */
  async treasureStart(request: TreasureStartRequest): Promise<TreasureStartResponse> {
    return this.post<TreasureStartResponse>('/games/treasure/start', request)
  }

  /**
   * Submit a treasure trial
   */
  async treasureTrial(request: TreasureTrialRequest): Promise<TreasureTrialResponse> {
    return this.post<TreasureTrialResponse>('/games/treasure/trial', request)
  }

  /**
   * Finish treasure game
   */
  async treasureFinish(request: TreasureFinishRequest): Promise<TreasureFinishResponse> {
    return this.post<TreasureFinishResponse>('/games/treasure/finish', request)
  }
}

/**
 * Default API service instance
 */
export const apiService = new ApiService()

/**
 * Create a new API service instance with custom configuration
 */
export function createApiService(config?: Partial<ApiConfig>): ApiService {
  return new ApiService(config)
}